import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material"
import { Actions, useStoreActions, useStoreState } from "easy-peasy"
import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { UpdateCustomerInput } from "../../graphql/types"
import { AppRoutes } from "../../store/routes"
import { StoreModel } from "../../store/store"

const DetailItem: React.FC<{
  label: string
  value?: string | null | number | undefined
  isEditable?: boolean
  name?: keyof UpdateCustomerInput
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  onSelectChange?: (
    event: React.ChangeEvent<{ name?: string; value: unknown }>
  ) => void
  options?: { value: string; label: string }[]
  type?: string
}> = ({
  label,
  value,
  isEditable,
  name,
  onChange,
  onSelectChange,
  options,
  type = "text",
}) => {
  if (isEditable && name) {
    if (options && onSelectChange) {
      return (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth margin="normal">
            <InputLabel id={`${name}-label`}>{label}</InputLabel>
            <Select
              labelId={`${name}-label`}
              name={name}
              value={value || ""}
              label={label}
              onChange={onSelectChange as any}
            >
              {options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )
    }
    return (
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={label}
          name={name}
          value={value || ""}
          onChange={onChange}
          margin="normal"
          type={type}
          InputLabelProps={type === "number" ? { shrink: true } : {}}
        />
      </Grid>
    )
  }
  return (
    <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        color="text.secondary"
        gutterBottom
        sx={{ fontWeight: "bold" }}
      >
        {label}
      </Typography>
      <Typography variant="body1" gutterBottom>
        {value !== undefined && value !== null ? String(value) : "N/A"}
      </Typography>
    </Grid>
  )
}

const CustomerDetailsPage: React.FC = () => {
  const { customerID: routeCustomerID } = useParams<{ customerID: string }>()
  const navigate = useNavigate()

  const customers = useStoreState((state: StoreModel) => state.customers)
  const fetchCustomers = useStoreActions(
    (actions: Actions<StoreModel>) => actions.fetchCustomers
  )

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        await fetchCustomers({ fresh: false })
      } catch (err) {
        console.error("Error loading customers:", err)
      }
    }
    loadCustomers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getCustomerByIdAction = useStoreActions(
    (actions: Actions<StoreModel>) => actions.getCustomerById
  )
  const updateCustomerAction = useStoreActions(
    (actions: Actions<StoreModel>) => actions.updateCustomer
  )

  const [isEditing, setIsEditing] = useState(false)
  const [editFormData, setEditFormData] = useState<
    Partial<UpdateCustomerInput>
  >({})
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const customer = customers.find(
    (c) => c.customerID === routeCustomerID || c.id === routeCustomerID
  )

  useEffect(() => {
    if (routeCustomerID) {
      if (!customer) {
        setLoading(true)
        console.log(
          `Customer with ID/TreoID '${routeCustomerID}' not in local store. Fetching by ID...`
        )
        getCustomerByIdAction({ customerID: routeCustomerID, fresh: true })
          .catch((err: unknown) => {
            console.error("Error fetching customer by ID from useEffect:", err)
            setError(
              `Could not load customer details: ${(err as Error).message}`
            )
          })
          .finally(() => {
            setLoading(false)
          })
      } else if (customer && !isEditing) {
        setEditFormData({
          customerName: customer.customerName || "",
          legalName: customer.legalName || "",
          status: customer.status || "ACTIVE",
          terms: customer.terms || "",
          customerClass: customer.customerClass || "INLAND",
          taxZone: customer.taxZone || "",
          locationName: customer.locationName || "",
          writeOffLimit: undefined,
        })
      }
    }
  }, [routeCustomerID, customer, getCustomerByIdAction, isEditing])

  const handleEditToggle = () => {
    if (isEditing && customer) {
      setEditFormData({
        customerName: customer.customerName || "",
        legalName: customer.legalName || "",
        status: customer.status || "ACTIVE",
        terms: customer.terms || "",
        customerClass: customer.customerClass || "INLAND",
        taxZone: customer.taxZone || "",
        locationName: customer.locationName || "",
        writeOffLimit: undefined,
      })
    }
    setIsEditing(!isEditing)
    setError(null)
    setSuccess(null)
  }

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = event.target
    setEditFormData((prev) => ({
      ...prev,
      [name]:
        type === "number"
          ? value === ""
            ? undefined
            : parseFloat(value)
          : value === ""
            ? null
            : value,
    }))
  }

  const handleSelectChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    const name = event.target.name as keyof UpdateCustomerInput
    const value = event.target.value
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmitEdit = async () => {
    if (!customer || !customer.customerID) {
      setError("Kunden Treo ID fehlt, kann nicht aktualisieren.")
      return
    }
    setIsSubmitting(true)
    setError(null)
    setSuccess(null)

    const updatePayload: UpdateCustomerInput = {
      customerID: customer.customerID,
      ...(editFormData.customerName && {
        customerName: editFormData.customerName,
      }),
      ...(editFormData.legalName && { legalName: editFormData.legalName }),
      ...(editFormData.status && { status: editFormData.status }),
      ...(editFormData.terms && { terms: editFormData.terms }),
      ...(editFormData.customerClass && {
        customerClass: editFormData.customerClass,
      }),
      ...(editFormData.taxZone && { taxZone: editFormData.taxZone }),
      ...(editFormData.locationName && {
        locationName: editFormData.locationName,
      }),
      ...(editFormData.writeOffLimit !== undefined && {
        writeOffLimit: editFormData.writeOffLimit,
      }),
    }

    try {
      const updatedCustomer = await updateCustomerAction(updatePayload)
      if (updatedCustomer) {
        setSuccess("Kunde erfolgreich aktualisiert!")
        setIsEditing(false)
      } else {
        setError("Failed to update customer. The operation returned no data.")
      }
    } catch (err: unknown) {
      setError(
        `Update failed: ${(err as Error).message || "An unexpected error occurred."}`
      )
    }
    setIsSubmitting(false)
  }

  const customerClassOptions = [
    { value: "INLAND", label: "INLAND" },
    { value: "EU", label: "EU" },
    { value: "DRITTLAND", label: "DRITTLAND" },
  ]

  const statusOptions = [
    { value: "ACTIVE", label: "Aktiv" },
    { value: "INACTIVE", label: "Inaktiv" },
    { value: "PROSPECT", label: "Interessent" },
  ]

  if (loading && !customer) {
    return (
      <Container sx={{ textAlign: "center", mt: 5 }}>
        <CircularProgress />
        <Typography>Kundendetails werden geladen...</Typography>
      </Container>
    )
  }

  if (!customer) {
    return (
      <Container sx={{ mt: 3 }}>
        <Alert severity="error">
          {error ||
            "Kunde nicht gefunden. Möglicherweise wurde er gelöscht oder die ID ist falsch."}
        </Alert>
        <Button onClick={() => navigate(AppRoutes.CUSTOMERS)} sx={{ mt: 2 }}>
          Back to Customers List
        </Button>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h4" gutterBottom component="div">
            {isEditing
              ? "Kunde bearbeiten"
              : customer.customerName || "Kundendetails"}
          </Typography>
          <Box>
            {isEditing ? (
              <>
                <Button
                  onClick={handleSubmitEdit}
                  variant="contained"
                  color="primary"
                  sx={{ mr: 1 }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Änderungen speichern"
                  )}
                </Button>
                <Button
                  onClick={handleEditToggle}
                  variant="outlined"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleEditToggle}
                sx={{ mr: 1 }}
              >
                Kunde bearbeiten
              </Button>
            )}
            {!isEditing && customer.customerID && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() =>
                  navigate(
                    `${AppRoutes.CUSTOMERS}/${customer.customerID}/create-contact`
                  )
                }
              >
                Add Contact
              </Button>
            )}
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {error && !isSubmitting && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Customer Information
        </Typography>
        <Grid container spacing={isEditing ? 0 : 2} alignItems="center">
          <DetailItem label="Kunden-ID (Treo)" value={customer.customerID} />
          <DetailItem label="Interne ID (GUID)" value={customer.id} />
          <DetailItem
            label="Kundenname"
            value={
              isEditing ? editFormData.customerName : customer.customerName
            }
            isEditable={isEditing}
            name="customerName"
            onChange={handleChange}
          />
          <DetailItem
            label="Offizieller Name"
            value={isEditing ? editFormData.legalName : customer.legalName}
            isEditable={isEditing}
            name="legalName"
            onChange={handleChange}
          />
          <DetailItem
            label="Status"
            value={isEditing ? editFormData.status : customer.status}
            isEditable={isEditing}
            name="status"
            onSelectChange={handleSelectChange}
            options={statusOptions}
          />
          <DetailItem
            label="Kundenklasse"
            value={
              isEditing ? editFormData.customerClass : customer.customerClass
            }
            isEditable={isEditing}
            name="customerClass"
            onSelectChange={handleSelectChange}
            options={customerClassOptions}
          />
          <DetailItem label="Währungs-ID" value={customer.currencyID} />
          <DetailItem
            label="Bedingungen"
            value={isEditing ? editFormData.terms : customer.terms}
            isEditable={isEditing}
            name="terms"
            onChange={handleChange}
          />
          <DetailItem
            label="Steuerzone"
            value={isEditing ? editFormData.taxZone : customer.taxZone}
            isEditable={isEditing}
            name="taxZone"
            onChange={handleChange}
          />
          <DetailItem
            label="Standortname"
            value={
              isEditing ? editFormData.locationName : customer.locationName
            }
            isEditable={isEditing}
            name="locationName"
            onChange={handleChange}
          />
          <DetailItem
            label="Abschreibungsgrenze"
            value={isEditing ? editFormData.writeOffLimit : undefined}
            isEditable={isEditing}
            name="writeOffLimit"
            type="number"
            onChange={handleChange}
          />
        </Grid>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Contact Information
        </Typography>
        <Grid container spacing={2}>
          <DetailItem label="E-Mail" value={customer.email} />
          <DetailItem label="Telefon" value={customer.phone1} />
        </Grid>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Address
        </Typography>
        <Grid container spacing={2}>
          <DetailItem label="Adresse Zeile 1" value={customer.addressLine1} />
          <DetailItem label="Stadt" value={customer.city} />
          <DetailItem label="Postleitzahl" value={customer.postalCode} />
          <DetailItem label="Land" value={customer.country} />
        </Grid>

        <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-start" }}>
          <Button
            variant="outlined"
            onClick={() => navigate(AppRoutes.CUSTOMERS)}
            disabled={isSubmitting}
          >
            Back to Customers List
          </Button>
        </Box>
      </Paper>
    </Container>
  )
}

export default CustomerDetailsPage
